import React, { useEffect, useState } from 'react';
import { PriceArea, PriceAreaInfo } from '../types';
import { getPriceAreaInfo } from '../utils/api';
import { MapPin, Loader, AlertCircle, MapPinOff } from 'lucide-react';

// Mapping of Norwegian counties (fylker, from 1.1.2024) to price areas.
// Price area borders don't follow county borders exactly, so this is an approximation.
const fylkeToPriceArea: Record<string, PriceArea> = {
  "Oslo": "NO1",
  "Akershus": "NO1",
  "Østfold": "NO1",
  "Buskerud": "NO1",
  "Innlandet": "NO1",
  "Vestfold": "NO1",
  "Telemark": "NO2",
  "Agder": "NO2",
  "Rogaland": "NO2",
  "Vestland": "NO5",
  "Møre og Romsdal": "NO3",
  "Trøndelag": "NO3",
  "Nordland": "NO4",
  "Troms": "NO4",
  "Finnmark": "NO4",
  // Counties dissolved 1.1.2024, in case the geocoder still returns them
  "Viken": "NO1",
  "Vestfold og Telemark": "NO2",
  "Troms og Finnmark": "NO4",
};

// Nominatim may return names with Sami/Kven variants, e.g. "Troms – Romsa – Tromssa"
const findPriceArea = (county: string): PriceArea | undefined => {
  const match = Object.keys(fylkeToPriceArea)
    .sort((a, b) => b.length - a.length)
    .find((name) => county === name || county.startsWith(`${name} `));
  return match ? fylkeToPriceArea[match] : undefined;
};

interface PriceAreaSelectorProps {
  selectedArea: PriceArea;
  onAreaChange: (area: PriceArea) => void;
}

const PriceAreaSelector: React.FC<PriceAreaSelectorProps> = ({
  selectedArea,
  onAreaChange,
}) => {
  const [locationDenied, setLocationDenied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showLocationButton, setShowLocationButton] = useState(true);
  const areas: PriceAreaInfo[] = getPriceAreaInfo();
  
  // Try to get location from localStorage first
  useEffect(() => {
    const savedArea = localStorage.getItem('selectedPriceArea') as PriceArea;
    if (savedArea && areas.some(area => area.id === savedArea)) {
      onAreaChange(savedArea);
    }
  }, []);

  const fetchLocation = async () => {
    if ("geolocation" in navigator) {
      setLoading(true);
      setShowLocationButton(false);
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
            );
            const data = await response.json();

            // Oslo is both a city and a county, so Nominatim only returns it as "city"
            const fylke = data.address?.county ?? data.address?.state ?? data.address?.city;
            if (fylke) {
              const matchedArea = findPriceArea(fylke);

              if (matchedArea) {
                setError(null);
                localStorage.setItem('selectedPriceArea', matchedArea);
                onAreaChange(matchedArea);
              } else {
                setError(`Fant ikke et prisområde for ${fylke}`);
              }
            } else {
              setError("Kunne ikke hente fylke-data.");
            }
          } catch (err) {
            console.error("Error fetching location:", err);
            setError("Kunne ikke finne din posisjon.");
          } finally {
            setLoading(false);
          }
        },
        () => {
          setLocationDenied(true);
          setError("Plassering er deaktivert eller ikke tilgjengelig.");
          setLoading(false);
        }
      );
    } else {
      setLocationDenied(true);
      setError("Geolokasjon støttes ikke av denne nettleseren.");
    }
  };

  // Save selected area to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('selectedPriceArea', selectedArea);
  }, [selectedArea]);

  return (
    <div className="space-y-2">
      {!locationDenied && !loading && (
        <button
          onClick={fetchLocation}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
        >
          <MapPin className="h-5 w-5" />
          <span>Finn mitt prisområde automatisk</span>
        </button>
      )}
      
      <div className="relative">
        <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
          <MapPin className="h-5 w-5 text-gray-400" />
        </div>
          <select
            id="area-select"
            value={selectedArea}
            onChange={(e) => onAreaChange(e.target.value as PriceArea)}
            className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm text-gray-900 dark:text-white appearance-none"
          >
            {areas.map((area) => (
              <option key={area.id} value={area.id}>
                {area.description}
              </option>
            ))}
          </select>
      </div>

      {loading && (
        <div className="flex items-center justify-center text-gray-500 dark:text-gray-400 py-2">
          <Loader className="w-4 h-4 animate-spin mr-2" />
          Henter din plassering...
        </div>
      )}

      {error && !locationDenied && (
        <p className="text-sm text-red-500 dark:text-red-400 mt-2 flex items-center justify-center">
          <AlertCircle className="w-4 h-4 mr-2" />
          {error}
        </p>
      )}
      
      {locationDenied && (
        <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center justify-center">
          <MapPinOff className="w-4 h-4 mr-2" />
          Velg ditt prisområde fra listen over
        </p>
      )}
    </div>
  );
};

export default PriceAreaSelector;